import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Drawer } from '../../app/Drawer';
import { Alert } from '../../components/ui/Alert';
import { ArtworkPicker } from '../../components/ui/ArtworkPicker';
import { Button } from '../../components/ui/Button';
import { Chip } from '../../components/ui/Card';
import { Explicit } from '../../components/ui/Explicit';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { SkeletonBlock, SkeletonText } from '../../components/ui/Loader';
import { RepostButton } from '../../components/ui/RepostButton';
import { Verified } from '../../components/ui/Verified';
import { ApiError, api } from '../../lib/api';
import { useDocumentTitle } from '../../lib/title';
import { usePlayer } from '../../stores/player';
import { comments as commentsApi } from '../comments/api';
import { CommentThread } from '../comments/CommentThread';
import { AddToPlaylist } from '../library/AddToPlaylist';
import { ReportButton } from '../moderation/ReportDialog';
import { Waveform } from '../player/Waveform';
import { SendToRoom } from '../rooms/SendToRoom';
import { Lyrics } from './Lyrics';
import { ProvenanceNote } from './provenance';
import { CreditsEditor } from './CreditsEditor';
import { CreditsList } from './CreditsList';
import { ShareEmbed } from './ShareEmbed';
import { TrackEditor } from './TrackEditor';
import { type TrackDetail, toPlayable } from './detail';
import { describeKey } from './keys';

/**
 * A track, in the side drawer.
 *
 * ## Everything the page knows, in a column
 *
 * It used to be a deliberate subset - artwork, title, play, comments - on the
 * reasoning that a 560px panel is not a two-column page. Which was right about
 * the layout and wrong about the content: the drawer is where almost every
 * track is actually opened from, so "the credits are on the full page" meant
 * nobody saw credits, nobody saw the tempo or the key, and an owner could not
 * fix a typo without leaving the list they were working through.
 *
 * So this shows what the page shows. It is one column instead of two and the
 * sharing block is folded away, but nothing is missing and nothing is a
 * different component: the waveform, the buttons, the credits, the editors and
 * the whole comment thread are the ones the page uses, so a fix to any of them
 * lands in both.
 *
 * ## What stays different, and why
 *
 * The cover runs the full width rather than sitting at 176px beside a column of
 * details - in a panel this narrow, a small square with three lines next to it
 * is a worse read than a picture with the facts under it. And the description
 * appears once here, where the page prints it twice (beside the cover and again
 * below), which in a single column would just be the same paragraph twice.
 */
export function TrackDrawer() {
  const { id = '' } = useParams();

  const current = usePlayer((s) => s.track);
  const playing = usePlayer((s) => s.playing);
  const position = usePlayer((s) => s.position);
  const duration = usePlayer((s) => s.duration);
  const play = usePlayer((s) => s.play);
  const toggle = usePlayer((s) => s.toggle);
  const seek = usePlayer((s) => s.seek);

  const [editing, setEditing] = useState(false);
  const [editingCredits, setEditingCredits] = useState(false);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['track', id],
    queryFn: () => api.get<TrackDetail>(`/tracks/${id}`),
    retry: false,
    /*
     * The same poll the page runs. A track opened from the upload screen lands
     * here still encoding, and without this the drawer says "still being
     * processed" until somebody thinks to close and reopen it.
     */
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      return status === 'processing' || status === 'pending' ? 3000 : false;
    },
  });

  // The same query key the thread below uses, so the pins on the waveform and
  // the comments under it come from one request.
  const { data: thread } = useQuery({
    queryKey: ['comments', Number(id)],
    queryFn: () => commentsApi.forTrack(Number(id)),
    enabled: id !== '' && data?.status === 'ready',
  });

  useDocumentTitle(data?.title ?? null);

  const isCurrent = current?.id === data?.id;
  const total = isCurrent ? duration || (data?.duration_ms ?? 0) / 1000 : (data?.duration_ms ?? 0) / 1000;

  return (
    <Drawer title={data?.title ?? 'Track'} fullPath={`/tracks/${id}`}>
      {isPending && (
        <div className="flex flex-col gap-5" aria-busy="true">
          <SkeletonBlock className="aspect-square w-full" />
          <SkeletonText lines={2} />
        </div>
      )}

      {error && (
        <Alert tone="error">
          {error instanceof ApiError ? error.message : 'That track could not be loaded.'}
        </Alert>
      )}

      {data && (
        <div className="flex flex-col gap-5">
          {/*
            The cover, and the way an owner changes it.

            `ArtworkPicker` rather than a plain `<img>` so that the one place
            somebody looks at their own track is also a place they can fix its
            artwork. For everybody else it renders exactly the image it did
            before, including the patterned placeholder when there is none.
          */}
          <ArtworkPicker
            subject="tracks"
            subjectId={data.id}
            artwork={data.artwork ?? data.artwork_small}
            size="full"
            refetch={() => api.get<TrackDetail>(`/tracks/${data.id}`)}
            editable={data.is_owner}
            onUploaded={() => void refetch()}
          />

          <div className="flex flex-col gap-1.5">
            <h2 className="flex items-center gap-2 text-2xl font-bold leading-tight tracking-tight text-ink">
              {data.explicit && <Explicit className="h-5 w-5 text-[13px]" />}
              {data.title}
            </h2>

            {data.owner && (
              <Link
                to={`/${data.owner.username}`}
                className="text-[15px] text-text-2 hover:text-ink hover:underline"
              >
                {data.owner.display_name}
                {data.owner.verified && <Verified />}
              </Link>
            )}

            <div className="flex flex-wrap items-center gap-2">
              {/*
                Said here as well as on the page, because it is the difference
                between "nobody has heard this" and "nobody can". An owner
                working through their own uploads should not have to open a
                second screen to find out which.
              */}
              {data.visibility !== 'public' && <Chip>{data.visibility}</Chip>}

              {data.status === 'ready' && (
                <span className="meta text-faint">
                  {data.play_count.toLocaleString()} {data.play_count === 1 ? 'play' : 'plays'}
                </span>
              )}
            </div>
          </div>

          {/*
            The four upload states, said the same way the page says them. A file
            that is up but not playable lasts minutes on audio, so it gets a
            sentence rather than a spinner - and a failure says what failed.
          */}
          {data.status === 'processing' || data.status === 'pending' ? (
            <Alert tone="info">
              Processing. We are building the streaming versions. This usually takes a minute or
              two, and this panel will update itself.
            </Alert>
          ) : data.status === 'failed' ? (
            <Alert tone="error">{data.failure_reason ?? 'Processing failed.'}</Alert>
          ) : (
            <>
              <Waveform
                url={data.waveform_url}
                progress={isCurrent && total > 0 ? Math.min(1, position / total) : 0}
                height={56}
                onSeek={isCurrent ? (fraction) => seek(fraction * total) : undefined}
                // §6.3's waveform pins - comments anchored to a moment, shown
                // where they were left. They were on the page and not here,
                // which made the same track look like two different recordings.
                pins={thread?.pins ?? []}
                durationMs={data.duration_ms}
                onPinClick={(pin) => seek(pin.position_ms / 1000)}
              />

              <div className="flex flex-wrap items-center gap-2.5">
                <Button
                  onClick={() => (isCurrent ? toggle() : void play(toPlayable(data), undefined, 'track'))}
                  data-drawer-play
                >
                  {isCurrent && playing ? 'Pause' : 'Play'}
                </Button>

                <FavoriteButton
                  trackId={data.id}
                  favorited={data.favorited}
                  likeCount={data.like_count}
                  showCount
                  size="button"
                  className="rounded-[var(--radius-pill)] border border-line px-4 py-2.5"
                />

                <RepostButton
                  title={data.title}
                  artist={data.owner?.display_name ?? null}
                  trackId={data.id}
                  reposted={data.reposted}
                  repostCount={data.repost_count}
                  showCount
                  // Rewinding publishes to your followers, so it only applies
                  // to something they can hear. The server refuses the rest.
                  disabled={data.visibility !== 'public'}
                />

                <AddToPlaylist trackId={data.id} title={data.title} size="sm" />

                {/*
                  Public only, for the same reason as rewinding: a room cannot
                  play what its listeners are not allowed to hear.
                */}
                {data.visibility === 'public' && (
                  <SendToRoom trackId={data.id} ownedByMe={data.is_owner} />
                )}
              </div>

              {typeof data.lyrics === 'string' && data.lyrics !== '' && (
                <Lyrics text={data.lyrics} />
              )}

              {/* Same rule as the page: stated or not, the line is there. */}
              <ProvenanceNote value={data.provenance ?? null} />

              {(data.bpm !== null || data.key_signature !== null) && (
                <div className="meta text-faint" data-track-tempo>
                  {[
                    data.bpm === null ? null : `${data.bpm} BPM`,
                    data.key_signature === null ? null : describeKey(data.key_signature),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}

              {data.loudness_lufs !== null && (
                <div className="meta text-faint">
                  source loudness {data.loudness_lufs} LUFS &middot; normalized to -14 for playback
                </div>
              )}
            </>
          )}

          {data.description && (
            <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-text-2">
              {data.description}
            </p>
          )}

          <CreditsList credits={data.credits} />

          {/*
            Sharing, folded.

            Same block as the page and the same rules - a public, finished track
            only, because an embed code for something still encoding renders
            "not available" on somebody's blog. Behind a button because the
            expanded form is four controls and a monospace box, and leaving that
            open in a column this narrow pushes the comments off the screen for
            everybody who was not sharing anything.
          */}
          {data.status === 'ready' && data.visibility === 'public' && (
            <ShareEmbed trackId={data.id} title={data.title} collapsible />
          )}

          <div className="-ml-2 flex flex-wrap items-center gap-1">
            {data.is_owner ? (
              <>
                <Button variant="ghost" data-drawer-edit onClick={() => setEditing(true)}>
                  Edit track
                </Button>
                <Button variant="ghost" data-drawer-credits onClick={() => setEditingCredits(true)}>
                  Credits
                </Button>
              </>
            ) : (
              // Understated, and never on your own track - reporting yourself
              // is not a thing anybody means to do.
              <div className="ml-2">
                <ReportButton subjectType="track" subjectId={data.id} title={data.title} />
              </div>
            )}
          </div>

          {data.status === 'ready' && (
            <div className="border-t border-line pt-5">
              <CommentThread trackId={data.id} />
            </div>
          )}

          {/*
            The same dialogs the page opens. `<dialog showModal>` puts them in
            the browser's top layer, so they sit over the drawer rather than
            inside its scroll container - see `Modal`, and the Escape guard in
            `Drawer` that stops one keypress closing both.
          */}
          {editingCredits && (
            <CreditsEditor
              trackId={data.id}
              credits={data.credits}
              onClose={() => setEditingCredits(false)}
              onSaved={() => {
                setEditingCredits(false);
                void refetch();
              }}
            />
          )}

          {editing && (
            <TrackEditor
              track={data}
              onClose={() => setEditing(false)}
              onSaved={() => {
                setEditing(false);
                void refetch();
              }}
            />
          )}
        </div>
      )}
    </Drawer>
  );
}
